import { Typography } from '@mui/material'
import { useCreateSettlementExportMutation } from 'api/settlementExports/settlementExports.api'
import {
  SETTLEMENT_EXPORT_MAX_PERIOD_DAYS,
  SETTLEMENT_TIME_ZONE,
} from 'api/settlementExports/settlementExports.constants'
import type { SettlementExportRecord } from 'api/settlementExports/settlementExports.types'
import { postStat } from 'api/stats/stats.api'
import React, { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import NewButton from 'ui/atoms/NewButton/NewButton'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { SettlementPeriodSummary } from 'ui/components/Followup/SettlementPeriodSummary/SettlementPeriodSummary'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import { SettlementDayRangePicker } from './SettlementDayRangePicker'
import { dayRangeExceedsDays, dayRangeHours, dayRangeToCreateBody } from './SettlementExportButton.helper'
import type { DayRange } from './SettlementExportButton.types'

type SettlementExportRunFormProps = {
  initialRange: DayRange
  /** `null` when the export was accepted but its response could not be read. */
  onCreated: (settlementExport: SettlementExportRecord | null) => void
}

export function SettlementExportRunForm({ initialRange, onCreated }: SettlementExportRunFormProps): ReactNode {
  const { t } = useTranslation()
  const { systemId, activeSystem } = useAuth()
  const [range, setRange] = useState(initialRange)
  const [isPickerInvalid, setIsPickerInvalid] = useState(false)
  const { mutate: createSettlementExport, isPending } = useCreateSettlementExportMutation()

  const exceedsMaxPeriod = dayRangeExceedsDays(range, SETTLEMENT_EXPORT_MAX_PERIOD_DAYS)

  function handleRun(): void {
    if (!systemId) return
    createSettlementExport(dayRangeToCreateBody(systemId, range), { onSuccess: onCreated })
    postStat(`settlement-export`, `run-settlement-export`)
  }

  return (
    <section className="flex flex-col gap-2">
      <Typography variant="h3">{t(`Run settlement export`)}</Typography>
      <div className="flex flex-wrap items-center gap-2">
        <SettlementDayRangePicker range={range} onRangeChange={setRange} onValidityChange={setIsPickerInvalid} />
        <NewButton
          variant="primary"
          label={t(`Run export`)}
          disabled={isPickerInvalid || exceedsMaxPeriod || !systemId}
          loading={isPending}
          onClick={handleRun}
        />
      </div>
      {/* The backend always builds the export in its own fixed zone, so the period does too; the
          system's zone only decides whether that can be called the system's time. */}
      <SettlementPeriodSummary
        text={
          activeSystem?.default_time_zone === SETTLEMENT_TIME_ZONE
            ? t(
                `The export covers {{hours}} hours, from 00:00 on the first day to 24:00 on the last day, system time ({{timeZone}}).`,
                { hours: dayRangeHours(range), timeZone: SETTLEMENT_TIME_ZONE }
              )
            : t(
                `The export covers {{hours}} hours, from 00:00 on the first day to 24:00 on the last day, {{timeZone}} time.`,
                { hours: dayRangeHours(range), timeZone: SETTLEMENT_TIME_ZONE }
              )
        }
      />
      {exceedsMaxPeriod && (
        <InfoBanner
          style="danger"
          text={t(`At most {{days}} days can be exported at once.`, { days: SETTLEMENT_EXPORT_MAX_PERIOD_DAYS })}
        />
      )}
    </section>
  )
}
